import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from './AuthContext';
import { setActiveCarrierId, workforceFetch } from '../lib/workforceBff';
import UnsavedChangesDialog from '../components/carrier/UnsavedChangesDialog';

const outsideShell = {
  inShell: false, carriers: [], carrier: null, loading: false, error: null,
  confirmLeave: async () => true, registerUnsaved: () => {}, clearUnsaved: () => {},
};
export const CarrierContext = createContext(outsideShell);
export function useCarrier() { return useContext(CarrierContext); }

async function carrierRequest(method, body, signal) {
  const { supabase } = await import('../lib/supabaseClient');
  const { data } = await supabase.auth.getSession();
  const headers = { Accept: 'application/json' };
  if (data?.session?.access_token) headers.Authorization = `Bearer ${data.session.access_token}`;
  if (body) headers['Content-Type'] = 'application/json';
  return workforceFetch(`/api/auth/carriers${method === 'PUT' ? '/default' : ''}`, {
    method, headers, signal, ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

export function CarrierProvider({ children }) {
  const { session, profile } = useAuth();
  const identity = `${session?.user?.id || profile?.id || ''}:${profile?.status || ''}:${profile?.role || ''}`;
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [readyId, setReadyId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const previous = useRef(null);
  const unsaved = useRef(new Map());
  const pendingLeave = useRef(null);
  const switched = useRef(null);
  const liveIdentity = useRef(identity);
  const mounted = useRef(false);
  const currentList = list?.identity === identity ? list : null;
  const loading = !currentList || currentList.loading;
  const error = currentList?.error || null;
  const carriers = currentList?.carriers || [];
  const defaultCarrier = carriers.find((item) => item.id === currentList?.defaultId) || null;
  const slug = new URLSearchParams(location.search).get('carrier');
  const remembered = previous.current?.identity === identity ? previous.current.carrier : null;
  const carrier = loading || error ? null : slug !== null
    ? carriers.find((item) => item.slug === slug) || null
    : carriers.find((item) => item.id === remembered?.id) || defaultCarrier
      || (carriers.length === 1 ? carriers[0] : null);
  const id = carrier?.id || null;

  useLayoutEffect(() => {
    liveIdentity.current = identity;
    setActiveCarrierId(id);
    setReadyId(id);
    if (carrier) previous.current = { identity, carrier };
    return () => setActiveCarrierId(null);
    // Only a changed principal or carrier creates a new request generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, identity]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingLeave.current?.resolve(false);
      pendingLeave.current = null;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setList({ identity, loading: true, carriers: [] });
    unsaved.current.clear();
    carrierRequest('GET', null, controller.signal).then(async (response) => {
      if (!response.ok) throw new Error('Carrier list unavailable');
      const payload = await response.json();
      if (!Array.isArray(payload.carriers)) throw new Error('Invalid carrier list');
      if (!controller.signal.aborted) setList({
        identity, loading: false,
        carriers: [...payload.carriers].sort((a, b) => a.brand_name.localeCompare(b.brand_name, 'fa')),
        defaultId: payload.default_carrier_id,
      });
    }).catch(() => {
      if (!controller.signal.aborted) setList({
        identity, loading: false, carriers: [], error: 'فهرست اپراتورها دریافت نشد.',
      });
    });
    return () => controller.abort();
  }, [identity, attempt]);

  useEffect(() => {
    if (slug === null && carrier) {
      const params = new URLSearchParams(location.search);
      params.set('carrier', carrier.slug);
      navigate({ pathname: location.pathname, search: `?${params}`, hash: location.hash }, { replace: true });
    }
  }, [carrier, slug, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (!carrier || readyId !== carrier.id || switched.current !== carrier.id) return;
    switched.current = null;
    carrierRequest('PUT', { carrier_id: carrier.id }).catch(() => {});
    setAnnouncement(`اپراتور فعال: ${carrier.brand_name}`);
    const timer = setTimeout(() => {
      const visible = (node) => !node.closest('dialog, [hidden], [aria-hidden="true"], [data-state="inactive"]');
      const heading = [...document.querySelectorAll('.admin-body h2, h2')].find(visible);
      const target = heading || [...document.querySelectorAll('.admin-body')].find(visible)
        || [...document.querySelectorAll('.carrier-switch select')].find(visible);
      if (target) {
        if (target.tagName !== 'SELECT') target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [carrier, readyId]);

  const registerUnsaved = useCallback((key, label) => unsaved.current.set(key, label), []);
  const clearUnsaved = useCallback((key) => unsaved.current.delete(key), []);
  const finishLeave = useCallback((leave) => {
    if (leave) unsaved.current.clear();
    pendingLeave.current?.resolve(leave);
    pendingLeave.current = null;
    setDialog(null);
  }, []);
  const confirmLeave = useCallback((destination) => {
    if (!unsaved.current.size) return Promise.resolve(true);
    if (pendingLeave.current) return pendingLeave.current.promise;
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    pendingLeave.current = { promise, resolve };
    setDialog({ labels: [...unsaved.current.values()], destination });
    return promise;
  }, []);
  const switchTo = async (nextSlug) => {
    const next = carriers.find((item) => item.slug === nextSlug);
    if (!next || next.id === id) return false;
    if (!await confirmLeave(next.brand_name) || !mounted.current || liveIdentity.current !== identity) return false;
    switched.current = next.id;
    navigate({ pathname: location.pathname, search: `?carrier=${encodeURIComponent(next.slug)}`, hash: '' }, { replace: true });
    return true;
  };

  return <CarrierContext.Provider value={{
    inShell: true, carriers, carrier, loading, error, readyId, defaultCarrier,
    invalidSlug: slug !== null && !loading && !error && !carrier ? slug : null,
    reload: () => setAttempt((value) => value + 1),
    switchTo, confirmLeave, registerUnsaved, clearUnsaved,
  }}>
    {children}
    <span className="carrier-sr-only" role="status" aria-live="polite">{announcement}</span>
    <div className="admin-root carrier-dialog-host"><UnsavedChangesDialog state={dialog} onDecision={finishLeave} /></div>
  </CarrierContext.Provider>;
}
